import { useLogout } from '@smarthome/api';
import { useSettingsStore } from '@smarthome/store';
import { ROUTES } from '@smarthome/ui';
import {
  Activity,
  Cpu,
  Gauge,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  PanelLeftClose,
  Settings,
  Sparkles,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link, useRouteError, matchPath } from 'react-router-dom';

import { NotificationBell } from '@/features/notifications';
import { useVersionCheck } from '@/hooks/useVersionCheck';

interface RootLayoutProps {
  /** When true, renders the error UI instead of Outlet (used as errorElement) */
  showError?: boolean;
}

/**
 * Root layout - Premium smart home design
 *
 * Inspired by Apple Home, Google Nest, and premium dashboard themes.
 * Key principles:
 * - Warmer neutrals instead of cold grays
 * - Generous spacing and breathing room
 * - Subtle depth through shadows
 * - Refined iconography
 */
export function RootLayout({ showError }: RootLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: logout } = useLogout();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { sidebarHidden, setSidebarHidden, toggleSidebar } = useSettingsStore();
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isBulkSensorsEdit = location.pathname === ROUTES.SENSORS_BULK_EDIT;
  const isDashboardRoute =
    location.pathname === ROUTES.HOME ||
    !!matchPath({ path: ROUTES.DASHBOARD_DETAIL, end: true }, location.pathname);
  const isDashboardPresentation = sidebarHidden && isDashboardRoute;
  const contentClassName = sidebarHidden
    ? 'w-full'
    : isBulkSensorsEdit
      ? 'w-full max-w-none'
      : 'mx-auto w-full max-w-6xl';
  const mainClassName = isDashboardPresentation
    ? 'flex min-h-0 flex-1 flex-col overflow-y-auto'
    : [
        'flex min-h-0 flex-1 flex-col overflow-y-auto',
        'px-4 pt-6 pb-6 sm:px-6 sm:pt-8 sm:pb-8 lg:px-10 lg:pb-10',
      ].join(' ');

  const startLongPress = () => {
    longPressTimer.current = setTimeout(() => setSidebarHidden(false), 600);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Version checking - auto-refreshes when backend version changes
  useVersionCheck();

  // Escape key to show sidebar when hidden (bubble phase - overlays use capture phase)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarHidden) {
        setSidebarHidden(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarHidden, setSidebarHidden]);

  function handleLogout() {
    logout(
      { data: {} },
      {
        onSettled: () => {
          // Navigate to login regardless of success/failure
          // Backend clears cookies on success; on failure we still want to redirect
          navigate(ROUTES.LOGIN);
        },
      }
    );
  }

  const navigation = [
    {
      name: 'Home',
      href: ROUTES.HOME,
      icon: ({ className, active }: { className?: string; active?: boolean }) => (
        <Home className={className} strokeWidth={active ? 2 : 1.75} />
      ),
    },
    {
      name: 'Dashboards',
      href: ROUTES.DASHBOARDS,
      icon: ({ className, active }: { className?: string; active?: boolean }) => (
        <LayoutDashboard className={className} strokeWidth={active ? 2 : 1.75} />
      ),
    },
    {
      name: 'Devices',
      href: ROUTES.DEVICES,
      icon: ({ className, active }: { className?: string; active?: boolean }) => (
        <Cpu className={className} strokeWidth={active ? 2 : 1.75} />
      ),
    },
    {
      name: 'Scenes',
      href: ROUTES.SCENES,
      icon: ({ className, active }: { className?: string; active?: boolean }) => (
        <Zap className={className} strokeWidth={active ? 2 : 1.75} />
      ),
    },
    {
      name: 'Sensors',
      href: ROUTES.SENSORS,
      icon: ({ className, active }: { className?: string; active?: boolean }) => (
        <Activity className={className} strokeWidth={active ? 2 : 1.75} />
      ),
    },
    {
      name: 'Utilities',
      href: ROUTES.UTILITIES,
      icon: ({ className, active }: { className?: string; active?: boolean }) => (
        <Gauge className={className} strokeWidth={active ? 2 : 1.75} />
      ),
    },
    {
      name: 'Settings',
      href: ROUTES.SETTINGS,
      icon: ({ className, active }: { className?: string; active?: boolean }) => (
        <Settings className={className} strokeWidth={active ? 2 : 1.75} />
      ),
    },
  ];

  return (
    <div className="flex h-full overflow-hidden bg-gray-50 dark:bg-[#111113]">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm dark:bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`safe-area-top safe-area-bottom safe-area-left fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b] lg:static lg:h-full ${
          sidebarHidden ? 'lg:-translate-x-full lg:hidden' : 'lg:translate-x-0'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-200`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between gap-3 px-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-orange-500/20">
              <Home className="h-5 w-5 text-white" fill="currentColor" />
            </div>
            <span className="text-[15px] font-semibold tracking-tight text-gray-900 dark:text-white">
              Smarthome
            </span>
          </div>
          {/* Hide sidebar button - desktop only */}
          <button
            onClick={toggleSidebar}
            className="hidden rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 lg:flex dark:text-zinc-500 dark:hover:bg-white/[0.06] dark:hover:text-zinc-300"
            title="Hide sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium transition-all duration-150 ${
                      isActive
                        ? 'bg-gray-100 text-gray-900 dark:bg-white/[0.08] dark:text-white'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-zinc-200'
                    }`}
                  >
                    <item.icon className="h-[18px] w-[18px]" active={isActive} />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User section */}
        <div className="border-t border-gray-200 p-3 dark:border-white/[0.06]">
          {/* Notifications */}
          <NotificationBell variant="sidebar" />
          {/* Design System Link - Development Only */}
          {import.meta.env.DEV && (
            <Link
              to={ROUTES.DESIGN_SYSTEM}
              onClick={() => setSidebarOpen(false)}
              className={`group mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium transition-all duration-150 ${
                location.pathname === ROUTES.DESIGN_SYSTEM
                  ? 'bg-amber-500/10 text-amber-500 dark:text-amber-400'
                  : 'text-gray-400 hover:bg-amber-500/5 hover:text-amber-500 dark:text-zinc-500 dark:hover:text-amber-400'
              }`}
            >
              <Sparkles className="h-[18px] w-[18px]" />
              Design System
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium text-gray-500 transition-all duration-150 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-zinc-200"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />
            Log out
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="safe-area-top safe-area-bottom safe-area-right flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile header */}
        {!isDashboardPresentation && (
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white/90 px-4 backdrop-blur-xl dark:border-white/[0.06] dark:bg-[#111113]/90 lg:hidden">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-white"
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>
              <span className="text-[15px] font-semibold tracking-tight text-gray-900 dark:text-white">
                Smarthome
              </span>
            </div>
            <NotificationBell />
          </header>
        )}

        {/* Content */}
        <main className={mainClassName}>
          <div className={contentClassName}>{showError ? <RouteErrorUI /> : <Outlet />}</div>
        </main>
      </div>

      {/* Invisible long-press zone for touch devices when sidebar is hidden */}
      {sidebarHidden && (
        <div
          onPointerDown={startLongPress}
          onPointerUp={cancelLongPress}
          onPointerLeave={cancelLongPress}
          onPointerCancel={cancelLongPress}
          className="fixed left-0 top-0 z-[60] h-8 w-10"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

/**
 * Error UI rendered when a route error occurs.
 * React Router automatically resets this when navigating to another route.
 */
function RouteErrorUI() {
  const error = useRouteError() as Error;

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50 p-8 dark:border-red-500/20 dark:bg-red-500/10">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
        <svg
          className="h-6 w-6 text-red-600 dark:text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h3 className="mb-2 text-lg font-semibold text-red-800 dark:text-red-300">
        Something went wrong
      </h3>
      <p className="mb-4 max-w-md text-center text-sm text-red-600 dark:text-red-400">
        An error occurred while rendering this page. Navigate to another page or refresh.
      </p>
      {error?.message && (
        <details className="mb-4 w-full max-w-md">
          <summary className="cursor-pointer text-xs text-red-500 dark:text-red-400">
            Show error details
          </summary>
          <pre className="mt-2 overflow-auto rounded bg-red-100 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
            {error.message}
          </pre>
        </details>
      )}
      <button
        onClick={() => window.location.reload()}
        className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
      >
        Refresh page
      </button>
    </div>
  );
}
